/**
 * Lightweight GPS helper.
 * - getCachedPosition(maxAgeMs=180000) returns a Promise resolving to
 *   { latitude, longitude, accuracy_m, captured_at } OR null if permission
 *   is denied / timeout / unsupported.
 * - Caches the last fix for `maxAgeMs` (default 3 min) so a burst of photo
 *   uploads doesn't trigger repeated permission/battery hits.
 */

let _lastFix = null;

const _readPosition = (timeoutMs = 8000) =>
  new Promise((resolve) => {
    if (!('geolocation' in navigator)) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const fix = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          captured_at: new Date(pos.timestamp).toISOString(),
        };
        _lastFix = { ...fix, __fetched: Date.now() };
        resolve(fix);
      },
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 120000 }
    );
  });

export const getCachedPosition = async (maxAgeMs = 180000) => {
  if (_lastFix && Date.now() - _lastFix.__fetched < maxAgeMs) {
    const { latitude, longitude, accuracy_m, captured_at } = _lastFix;
    return { latitude, longitude, accuracy_m, captured_at };
  }
  return _readPosition();
};

export const clearGpsCache = () => { _lastFix = null; };
